import { reportClientError, setTelemetryToken } from './telemetry'

type FetchOptions = Omit<RequestInit, 'body'> & { body?: unknown }

const API_BASE = '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly statusText: string
  readonly body: string

  constructor(status: number, statusText: string, body: string) {
    super(`API ${status} ${statusText}: ${body}`)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.body = body
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  getToken: () => Promise<string | null>,
  opts: FetchOptions = {},
): Promise<T> {
  const token = await getToken()
  // Every request refreshes the identity a later report will carry — including
  // reports from render crashes and window errors, which have no token of their own.
  setTelemetryToken(token)
  const headers = new Headers(opts.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (opts.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  // Never report failures of the sink itself — that is how you get a loop.
  const isSink = path.startsWith('/client-errors')

  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers,
      body: opts.body == null ? undefined : JSON.stringify(opts.body),
    })
  } catch (err) {
    // The request never completed: offline, DNS, TLS, aborted connection. These
    // are invisible everywhere else — no server log ever sees them.
    if (!isSink) {
      reportClientError({
        kind: 'network',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? (err.stack ?? null) : null,
        apiPath: path,
      })
    }
    throw err
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    // 404 is "no such thing", not a fault — Settings relies on it for
    // "Курсів ще немає", so reporting it would bury the real signal in noise.
    if (!isSink && res.status !== 404) {
      reportClientError({
        kind: 'api',
        message: `${res.status} ${res.statusText}: ${text.slice(0, 200)}`,
        apiPath: path,
        apiStatus: res.status,
      })
    }
    throw new ApiError(res.status, res.statusText, text)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
